import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { InvestmentModal } from '../components/InvestmentModal';
import { DonorScroll } from '../components/DonorScroll';
import { useWallet } from '../context/WalletContext';
import { useNavigate } from 'react-router-dom';
import { useCollectiveInvestment } from '../context/CollectiveInvestmentContext';
import { SearchResults } from '../components/SearchResults';
import { useAuth } from '../context/AuthContext';
import { useInvestmentPrice } from '../hooks/useInvestmentPrice';
import { collection, onSnapshot, query, where, orderBy, limit, doc, getDoc, increment, arrayUnion, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useCurrency } from '../hooks/useCurrency';
import { search } from '../utils/search';
import { SearchResult } from '../utils/search';
import { Donation } from '../types';
import { useDrawAdmin } from '../context/DrawAdminContext';
import { GeneralDonation } from '../components/GeneralDonation';
import { getGeneralDonationData } from '../config/firebase';
import type { GeneralDonationData } from '../types/generalDonation';
import { navigateToPublicProfile } from '../utils/profileUtils';

export interface Participant {
  userId: string;
  drawNumber: number;
}

export function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<any>>([]);
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);
  const { balance } = useWallet();
  const { 
    totalInvestment: projectedFunds, 
    participants: activeParticipants, 
    hasParticipated 
  } = useCollectiveInvestment();
  const navigate = useNavigate();
  const { user } = useAuth();
  const investmentPrice = useInvestmentPrice();
  const currency = useCurrency();
  const { canInitiateDraw, isParticipationEnabled, toggleParticipation } = useDrawAdmin();
  const [donationData, setDonationData] = useState<GeneralDonationData | null>(null);
  const [activeCard, setActiveCard] = useState<'investment' | 'donation'>('investment');

  useEffect(() => {
    // Calculer la date d'il y a 30 jours
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Écouter les donations récentes
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'recentDonations'),
        where('is_public', '==', true),
        where('timestamp', '>=', thirtyDaysAgo),
        orderBy('timestamp', 'desc'),
        limit(10)
      ),
      (snapshot) => {
        const donations: Donation[] = [];
        snapshot.forEach((doc) => {
          donations.push({ id: doc.id, ...doc.data() } as Donation);
        });
        setRecentDonations(donations);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    console.log('Current user:', user);
  }, [user]);

  useEffect(() => {
    const fetchDonationData = async () => {
      const data = await getGeneralDonationData();
      if (data) {
        setDonationData(data);
        // Afficher la donation générale par défaut si elle est active
        if (data.isActive) {
          setActiveCard('donation');
        }
      }
    };
    fetchDonationData();
  }, []);

  const handleInvestment = async () => {
    if (!isParticipationEnabled) {
      return;
    }

    if (!user) {
      alert('Vous devez être connecté pour participer');
      navigate('/login');
      return;
    }

    if (balance < investmentPrice) {
      alert(`Solde insuffisant. Il vous faut ${investmentPrice} ${currency} pour participer.`);
      return;
    }

    try {
      const batch = writeBatch(db);
      const investmentRef = doc(db, 'collectiveInvestment', 'current');
      const docSnap = await getDoc(investmentRef);
      const currentParticipants = docSnap.data()?.participants || [];
      
      const userId = user.id || user.uid;
      
      if (!currentParticipants.includes(userId)) {
        // Créer la transaction dans la sous-collection transactions
        const transaction = {
          type: 'investment',
          amount: -investmentPrice,
          date: new Date(),
          title: 'Participation aux donations collectives',
          status: 'completed'
        };

        // Ajouter la transaction à la sous-collection
        batch.set(doc(collection(db, 'users', userId, 'transactions')), transaction);

        batch.update(doc(db, 'wallets', userId), {
          balance: increment(-investmentPrice)
        });

        batch.update(investmentRef, {
          totalAmount: increment(investmentPrice),
          participantsCount: increment(1),
          participants: arrayUnion(userId)
        });

        await batch.commit();
        alert('Participation enregistrée avec succès !');
      } else {
        alert('Vous avez déjà participé à ce tirage.');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (!query) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await search(query, user);
      setSearchResults(results);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setSearchResults([]);
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    if (result.type === 'user') {
      navigateToPublicProfile(result.data.id, navigate);
    } else if (result.type === 'request') {
      navigate(`/2dr/request/${result.data.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-2">
        {/* Search Bar */}
        <div className="relative pt-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/8 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Rechercher dans magraine"
            className="w-full pl-14 pr-4 py-2 rounded-xl bg-white/5 text-white placeholder-gray-400 border border-white/10 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50"
          />
          <SearchResults
            results={searchResults}
            onSelect={handleSelectResult}
          />
        </div>

        {/* Recent Donors avec VIP badge - réduire la marge */}
        {recentDonations.length > 0 && (
          <div className="py-0">
            <DonorScroll donations={recentDonations} scrollSpeed="slow" />
          </div>
        )}

        {/* Carrousel avec flèches */}
        <div className="relative">
          <button 
            onClick={() => setActiveCard(activeCard === 'investment' ? 'donation' : 'investment')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-white/75 hover:text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="overflow-hidden">
            {activeCard === 'investment' ? (
              /* Votre carte d'investissement existante */
              <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-2">Donations Collectives</h2>
                <p className="text-gray-400 mb-4">Disponible</p>

                <div className="space-y-2 mb-6">
                  <div>
                    <p className="text-green-400 text-4xl font-bold">
                      {projectedFunds.toLocaleString()} {currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">
                      {activeParticipants} participants
                    </p>
                  </div>
                </div>

                <button 
                  onClick={() => handleInvestment()}
                  className={`w-full font-semibold py-4 px-6 rounded-xl transition-colors ${
                    !isParticipationEnabled
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : hasParticipated 
                        ? 'bg-green-300 hover:bg-green-400 cursor-not-allowed'
                        : balance >= investmentPrice
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'bg-gray-600 cursor-not-allowed text-gray-400'
                  }`}
                  disabled={!isParticipationEnabled || hasParticipated || balance < investmentPrice}
                >
                  {!isParticipationEnabled
                    ? 'Participation impossible'
                    : hasParticipated 
                      ? `Participation active (${investmentPrice} ${currency})`
                      : balance >= investmentPrice 
                        ? `Participer pour ${investmentPrice} ${currency}`
                        : 'Solde insuffisant'}
                </button>
                
                <p className="text-gray-400 text-sm text-center mt-4 italic">
                  {!isParticipationEnabled 
                    ? "Participation désactivée pour tirage au sort en cours..."
                    : "En participant, vous êtes sélectionné pour le tirage au sort ; vous pourriez être parmi les gagnants, ou pas"}
                </p>

                {/* Nouveau bouton toggle */}
                {canInitiateDraw && (
                  <div className="mt-8 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white text-sm font-medium">Autorisation de participation :</h3>
                        <p className="text-gray-400 text-xs">Activer/Désactiver les participations</p>
                      </div>
                      <button 
                        onClick={toggleParticipation}
                        className={`p-2 rounded-lg transition-colors ${
                          isParticipationEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        <div className="w-12 h-6 bg-white/10 rounded-full relative">
                          <div className={`absolute w-5 h-5 rounded-full top-0.5 left-0.5 transition-transform ${
                            isParticipationEnabled ? 'bg-green-400 translate-x-6' : 'bg-gray-400'
                          }`} />
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              activeCard === 'donation' && donationData ? (
                <GeneralDonation data={donationData} />
              ) : (
                <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/10">
                  {/* Carte d'investissement */}
                  <h2 className="text-2xl font-bold text-white mb-2">Donations Collectives</h2>
                  <p className="text-gray-400 mb-4">Disponible</p>

                  <div className="space-y-2 mb-6">
                    <div>
                      <p className="text-green-400 text-4xl font-bold">
                        {projectedFunds.toLocaleString()} {currency}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">
                        {activeParticipants} participants
                      </p>
                    </div>
                  </div>

                  <button 
                    onClick={() => handleInvestment()}
                    className={`w-full font-semibold py-4 px-6 rounded-xl transition-colors ${
                      !isParticipationEnabled
                        ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                        : hasParticipated 
                          ? 'bg-green-300 hover:bg-green-400 cursor-not-allowed'
                          : balance >= investmentPrice
                            ? 'bg-green-500 hover:bg-green-600 text-white'
                            : 'bg-gray-600 cursor-not-allowed text-gray-400'
                    }`}
                    disabled={!isParticipationEnabled || hasParticipated || balance < investmentPrice}
                  >
                    {!isParticipationEnabled
                      ? 'Participation impossible'
                      : hasParticipated 
                        ? `Participation active (${investmentPrice} ${currency})`
                        : balance >= investmentPrice 
                          ? `Participer pour ${investmentPrice} ${currency}`
                          : 'Solde insuffisant'}
                  </button>
                  
                  <p className="text-gray-400 text-sm text-center mt-4 italic">
                    {!isParticipationEnabled 
                      ? "Participation désactivée pour tirage au sort en cours..."
                      : "En participant, vous êtes sélectionné pour le tirage au sort ; vous pourriez être parmi les gagnants, ou pas"}
                  </p>

                  {/* Nouveau bouton toggle */}
                  {canInitiateDraw && (
                    <div className="mt-8 border-t border-white/10 pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white text-sm font-medium">Autorisation de participation :</h3>
                          <p className="text-gray-400 text-xs">Activer/Désactiver les participations</p>
                        </div>
                        <button 
                          onClick={toggleParticipation}
                          className={`p-2 rounded-lg transition-colors ${
                            isParticipationEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          <div className="w-12 h-6 bg-white/10 rounded-full relative">
                            <div className={`absolute w-5 h-5 rounded-full top-0.5 left-0.5 transition-transform ${
                              isParticipationEnabled ? 'bg-green-400 translate-x-6' : 'bg-gray-400'
                            }`} />
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          <button 
            onClick={() => setActiveCard(activeCard === 'investment' ? 'donation' : 'investment')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-white/75 hover:text-white"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Indicateurs de progression */}
        <div className="flex justify-center gap-2 mt-2">
          <button 
            className={`w-2 h-2 rounded-full transition-all ${activeCard === 'investment' ? 'bg-green-500 scale-125' : 'bg-gray-500/50'}`} 
            onClick={() => setActiveCard('investment')}
            aria-label="Voir donations collectives"
          />
          {donationData?.isActive && (
            <button 
              className={`w-2 h-2 rounded-full transition-all ${activeCard === 'donation' ? 'bg-green-500 scale-125' : 'bg-gray-500/50'}`}
              onClick={() => setActiveCard('donation')} 
              aria-label="Voir donation"
            />
          )}
        </div>

        <InvestmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onValidate={handleInvestment}
        />
      </div>
    </div>
  );
}